import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Car, Home, HeartPulse, Heart, Umbrella, Shield, Plus, Trash2, Plane } from "lucide-react";

const CAT = {
  auto: { icon: Car, color: "text-blue-500 bg-blue-50", label: "Auto" },
  home: { icon: Home, color: "text-teal-500 bg-teal-50", label: "Home" },
  renters: { icon: Home, color: "text-teal-500 bg-teal-50", label: "Renters" },
  life: { icon: Heart, color: "text-indigo-500 bg-indigo-50", label: "Life" },
  health: { icon: HeartPulse, color: "text-rose-500 bg-rose-50", label: "Health" },
  disability: { icon: Shield, color: "text-amber-500 bg-amber-50", label: "Disability" },
  umbrella: { icon: Umbrella, color: "text-purple-500 bg-purple-50", label: "Umbrella" },
  travel: { icon: Plane, color: "text-sky-500 bg-sky-50", label: "Travel" },
  pet: { icon: Heart, color: "text-pink-500 bg-pink-50", label: "Pet" },
  business: { icon: Shield, color: "text-slate-500 bg-slate-100", label: "Business" },
};
const catInfo = (c) => CAT[(c || "").toLowerCase()] || { icon: Shield, color: "text-slate-500 bg-slate-100", label: c };
const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");

export default function Wallet() {
  const { t } = useI18n();
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "auto", carrier: "", monthly_premium: "", deductible: "", coverage_limit: "", renewal_date: "" });

  const load = () => api.get("/policies").then((r) => setPolicies(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const payload = { ...form,
        monthly_premium: form.monthly_premium ? parseFloat(form.monthly_premium) : null,
        deductible: form.deductible ? parseFloat(form.deductible) : null,
        coverage_limit: form.coverage_limit ? parseFloat(form.coverage_limit) : null };
      await api.post("/policies", payload);
      toast.success(t("policy_added"));
      setOpen(false); setForm({ category: "auto", carrier: "", monthly_premium: "", deductible: "", coverage_limit: "", renewal_date: "" });
      load();
    } catch { toast.error(t("policy_add_error")); }
  };

  const del = async (id) => { await api.delete(`/policies/${id}`); load(); };

  return (
    <AppLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-navy">{t("my_wallet")}</h1>
          <p className="mt-1 text-slate-500">{t("wallet_sub")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-policy-btn" className="rounded-full brand-gradient text-white"><Plus className="h-4 w-4 mr-1" /> {t("add_policy")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add_a_policy")}</DialogTitle>
              <DialogDescription>{t("add_policy_desc")}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="policy-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CAT).map((k) => <SelectItem key={k} value={k}>{t("cat_" + k)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("carrier")}</Label><Input data-testid="policy-carrier" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("monthly_premium_amt")}</Label><Input data-testid="policy-premium" type="number" value={form.monthly_premium} onChange={(e) => setForm({ ...form, monthly_premium: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("deductible_amt")}</Label><Input data-testid="policy-deductible" type="number" value={form.deductible} onChange={(e) => setForm({ ...form, deductible: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("coverage_limit_amt")}</Label><Input type="number" value={form.coverage_limit} onChange={(e) => setForm({ ...form, coverage_limit: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("renewal_date")}</Label><Input data-testid="policy-renewal" type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} className="mt-1" /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="save-policy-btn" onClick={add} className="rounded-full brand-gradient text-white">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {policies.map((p) => {
          const ci = catInfo(p.category);
          return (
            <div key={p.id} data-testid={`policy-${p.id}`} className="rounded-2xl bg-white border border-slate-100 card-shadow p-5">
              <div className="flex items-center gap-3">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${ci.color}`}><ci.icon className="h-5 w-5" /></span>
                <div><p className="font-semibold text-navy">{t("cat_" + (p.category || "").toLowerCase())}</p><p className="text-xs text-slate-400">{p.carrier || "—"}</p></div>
                <span className="ml-auto h-2.5 w-2.5 rounded-full bg-covered" />
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <Row k={t("premium")} v={`${money(p.monthly_premium)}/mo`} />
                <Row k={t("deductible")} v={money(p.deductible)} />
                <Row k={t("coverage")} v={money(p.coverage_limit)} />
                <Row k={t("renewal")} v={p.renewal_date || "—"} />
                {p.policy_number && <Row k={t("policy_hash")} v={p.policy_number} />}
              </div>
              <button onClick={() => del(p.id)} data-testid={`del-policy-${p.id}`} className="mt-4 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /> {t("delete")}</button>
            </div>
          );
        })}
        {policies.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">{t("no_policies_add")}</div>}
      </div>
    </AppLayout>
  );
}
const Row = ({ k, v }) => (<div className="flex justify-between"><span className="text-slate-400">{k}</span><span className="font-medium text-navy">{v}</span></div>);
